/**
 * TV / 10-foot modu tercihi.
 *
 * Mod yalnız "ekran geniş" diye açılmaz: geniş bir masaüstü monitörü TV
 * değildir. `auto` şu iki kanıttan birini arar:
 *
 *   1. Tarayıcı/cihaz TV olduğunu söylüyor (SmartTV, Tizen, webOS, AppleTV…).
 *   2. Kullanıcı TV kumandasına özgü bir tuş gönderdi (GoBack, MediaPlayPause,
 *      renk tuşları…). Sıradan ok tuşları bu amaçla kullanılmaz.
 *
 * Kullanıcı isterse Hesap ekranından açıkça `on`/`off` seçebilir.
 */
import { useEffect, useMemo, useState } from 'react';
import { recordSwallowed } from './swallowed.ts';

export type TvModePreference = 'auto' | 'on' | 'off';

export const TV_MODE_STORAGE_KEY = 'pbfork.tvMode.v1';

const TV_USER_AGENT =
  /(smart-?tv|smarttv|googletv|android tv|appletv|tizen|webos|netcast|viera|bravia|hbbtv|roku|firetv|crkey)/i;

const REMOTE_KEYS = new Set([
  'GoBack',
  'BrowserBack',
  'MediaPlayPause',
  'MediaStop',
  'MediaTrackNext',
  'MediaTrackPrevious',
  'ColorF0Red',
  'ColorF1Green',
  'ColorF2Yellow',
  'ColorF3Blue',
]);

export function isTvUserAgent(userAgent: string): boolean {
  return TV_USER_AGENT.test(userAgent);
}

export function isRemoteKey(key: string): boolean {
  return REMOTE_KEYS.has(key);
}

export function sanitizeTvModePreference(value: unknown): TvModePreference {
  return value === 'on' || value === 'off' || value === 'auto' ? value : 'auto';
}

function readPreference(): TvModePreference {
  if (typeof localStorage === 'undefined') return 'auto';
  try {
    return sanitizeTvModePreference(localStorage.getItem(TV_MODE_STORAGE_KEY));
  } catch (error) {
    recordSwallowed('tvMode', error);
    return 'auto';
  }
}

export interface TvModeApi {
  enabled: boolean;
  preference: TvModePreference;
  setPreference: (preference: TvModePreference) => void;
  /** Cihaz/UA TV olarak tanındı mı? */
  detected: boolean;
}

export function useTvMode(): TvModeApi {
  const [preference, setPreferenceState] = useState<TvModePreference>(readPreference);
  const [remoteDetected, setRemoteDetected] = useState(false);
  const detected = useMemo(
    () => typeof navigator !== 'undefined' && isTvUserAgent(navigator.userAgent),
    [],
  );

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (isRemoteKey(event.key)) setRemoteDetected(true);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const enabled = preference === 'on' || (preference === 'auto' && (detected || remoteDetected));

  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle('tv-mode', enabled);
    return () => root.classList.remove('tv-mode');
  }, [enabled]);

  const setPreference = (next: TvModePreference): void => {
    setPreferenceState(next);
    try {
      localStorage.setItem(TV_MODE_STORAGE_KEY, next);
    } catch (error) {
      recordSwallowed('tvMode', error);
      // Gizli mod: tercih bu oturumda geçerli.
    }
  };

  return { enabled, preference, setPreference, detected };
}
